import { useEffect, useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { computeLevel } from "@shared/xp";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { MessageSquare, ArrowLeft, UserPlus, UserCheck, Clock, Swords } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ProfilePhotoViewer } from "@/components/ProfilePhotoViewer";
import { OnlineDot } from "@/components/OnlineDot";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { ChallengeDialog } from "@/components/ChallengeDialog";
import { StudentEmptyState } from "@/components/StudentEmptyState";
import { ProfileHero, ProfileStatTiles, SubjectMastery, BadgesSection, ConnectionsCard, PodiumRibbon, Initials, type ConnectionUser } from "@/components/profile/ProfileSections";

interface UserProfileData {
  user: { id: string; firstName: string; lastName: string; username: string; grade: string; school: string; profilePhotoUrl?: string; bio?: string | null; currentStreak?: number; role?: string; createdAt?: string };
  stats: { totalPoints: number; totalCorrect: number; totalQuestions: number; accuracy: number; currentLevel: number; currentXP: number; subjectBreakdown: Array<{ subject: string; points: number; fatigue: number }> };
  rank: number | null;
  totalPoints: number;
  followerCount: number;
  followingCount: number;
  achievements: Array<{ type: string; earnedAt: string }>;
}

export default function UserProfile() {
  const { userId } = useParams<{ userId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [viewingPhoto, setViewingPhoto] = useState<{ url: string; name: string } | null>(null);
  const [challengeDialogOpen, setChallengeDialogOpen] = useState(false);

  // Redirect to own profile page if viewing yourself
  useEffect(() => {
    if (userId && currentUser?.id && userId === currentUser.id) navigate("/profile", { replace: true });
  }, [userId, currentUser?.id]);

  const { data: profile, isLoading } = useQuery<UserProfileData>({ queryKey: ["/api/profile", userId], enabled: !!userId && userId !== currentUser?.id });
  const { data: followStatus } = useQuery<{ status: "none" | "pending" | "accepted" | "follows_you"; followId: string | null }>({ queryKey: ["/api/social/follow-status", userId], enabled: !!userId });
  const { data: connections } = useQuery<{ followers: ConnectionUser[]; following: ConnectionUser[] }>({ queryKey: ["/api/social/connections", userId], enabled: !!userId });

  const followMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/social/follow/${userId}`)).json(),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/social/follow-status", userId] }); queryClient.invalidateQueries({ queryKey: ["/api/profile", userId] }); toast({ title: "Follow request sent" }); },
    onError: () => toast({ title: "Could not send follow request", variant: "destructive" }),
  });
  const unfollowMutation = useMutation({
    mutationFn: async () => { await apiRequest("DELETE", `/api/social/follow/${userId}`); },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/social/follow-status", userId] }); queryClient.invalidateQueries({ queryKey: ["/api/profile", userId] }); toast({ title: "Unfollowed" }); },
  });
  const startConversationMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/messages/conversation", { recipientId: userId })).json(),
    onSuccess: async (data) => { await queryClient.invalidateQueries({ queryKey: ["/api/messages/conversations"] }); navigate(`/messages?conversation=${data.id}`); },
    onError: () => toast({ title: "Failed to start conversation", variant: "destructive" }),
  });
  const { isOnline } = useOnlineStatus(userId ? [userId] : []);

  if (isLoading || userId === currentUser?.id) {
    return <div className="container mx-auto p-4 max-w-5xl"><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div></div>;
  }
  if (!profile) {
    return (
      <div className="container mx-auto p-4 max-w-5xl">
        <StudentEmptyState illustration="owl-search" title="Owlbert could not find that profile." hint="It may be in another grade, or the link is old." action={<Button onClick={() => navigate("/dashboard")}>Back to dashboard</Button>} />
      </div>
    );
  }

  const u = profile.user;
  const level = computeLevel(profile.stats.totalPoints).currentLevel;
  const joined = u.createdAt ? new Date(u.createdAt).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : null;
  const followButton = (() => {
    const s = followStatus?.status;
    if (s === "pending") return <Button size="sm" variant="secondary" className="rounded-lg bg-white/20 hover:bg-white/25 text-primary-foreground" disabled><Clock className="h-4 w-4 mr-1.5" />Request sent</Button>;
    if (s === "accepted") return <Button size="sm" variant="secondary" className="rounded-lg bg-white/20 hover:bg-white/25 text-primary-foreground" onClick={() => unfollowMutation.mutate()} disabled={unfollowMutation.isPending}><UserCheck className="h-4 w-4 mr-1.5" />Following</Button>;
    if (s === "follows_you") return <Button size="sm" className="rounded-lg bg-white text-primary hover:bg-white/90" onClick={() => followMutation.mutate()} disabled={followMutation.isPending}><UserPlus className="h-4 w-4 mr-1.5" />Follow back</Button>;
    return <Button size="sm" className="rounded-lg bg-white text-primary hover:bg-white/90" onClick={() => followMutation.mutate()} disabled={followMutation.isPending} data-testid="button-follow"><UserPlus className="h-4 w-4 mr-1.5" />Follow</Button>;
  })();

  return (
    <>
      <div className="container mx-auto p-4 md:p-6 max-w-5xl space-y-4 pb-24 md:pb-8">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="-ml-2" data-testid="button-back-to-dashboard"><ArrowLeft className="h-4 w-4 mr-2" />Back to dashboard</Button>

        <ProfileHero
          user={u} rank={profile.rank} totalPoints={profile.totalPoints} level={level}
          followerCount={profile.followerCount} followingCount={profile.followingCount} joinedLabel={joined}
          onCounts={() => document.getElementById("connections")?.scrollIntoView({ behavior: "smooth" })}
          avatar={
            <div className="relative">
              <button type="button" className={`rounded-full ring-4 ring-white/40 ${u.profilePhotoUrl ? "cursor-zoom-in" : "cursor-default"}`} onClick={() => u.profilePhotoUrl && setViewingPhoto({ url: u.profilePhotoUrl, name: `${u.firstName} ${u.lastName}` })} aria-label="View photo">
                <Initials user={u} onPurple className="h-24 w-24 md:h-28 md:w-28 text-3xl" />
              </button>
              <OnlineDot isOnline={isOnline(userId ?? "")} className="absolute bottom-1 right-1 w-4 h-4 border-2 border-primary" />
            </div>
          }
          actions={
            <>
              <Button size="sm" className="rounded-lg bg-white text-primary hover:bg-white/90" onClick={() => startConversationMutation.mutate()} disabled={startConversationMutation.isPending} data-testid="button-send-message"><MessageSquare className="h-4 w-4 mr-1.5" />Message</Button>
              {followButton}
              <Button size="sm" variant="secondary" className="rounded-lg bg-white/20 hover:bg-white/25 text-primary-foreground" onClick={() => setChallengeDialogOpen(true)} data-testid="button-challenge"><Swords className="h-4 w-4 mr-1.5" />Challenge</Button>
            </>
          }
        />
        <PodiumRibbon rank={profile.rank} grade={u.grade} />
        <ProfileStatTiles accuracy={profile.stats.accuracy} totalQuestions={profile.stats.totalQuestions} totalCorrect={profile.stats.totalCorrect} streak={u.currentStreak ?? 0} badges={profile.achievements?.length ?? 0} xp={profile.stats.totalPoints} />
        <SubjectMastery subjects={profile.stats.subjectBreakdown ?? []} ownerFirstName={u.firstName} />
        <BadgesSection achievements={profile.achievements ?? []} isSelf={false} ownerFirstName={u.firstName} />
        <ConnectionsCard id="connections" isSelf={false} following={connections?.following ?? []} followers={connections?.followers ?? []} onOpen={x => navigate(`/user/${x.id}`)} />
      </div>

      <ProfilePhotoViewer url={viewingPhoto?.url ?? null} name={viewingPhoto?.name ?? ""} onClose={() => setViewingPhoto(null)} />
      <ChallengeDialog open={challengeDialogOpen} onOpenChange={setChallengeDialogOpen} opponentId={userId ?? ""} opponentName={`${u.firstName} ${u.lastName}`} userGrade={currentUser?.grade ?? 4} />
    </>
  );
}
